import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import axioInstance from "../../../../../../../../../custom_utilities/AxiosInstance/axiosInstance";
import {
  url_304,
  url_store_package_deltaupgrade,
} from "../../../../../../../../../custom_utilities/api_services";
import { formatExpiryDate, isPackageSubscribed } from "../StoreUtils/storePackageUtils";
import dividerIcon from "../../../../../../../../../utilities/images/package_plan_checkout/icon-divider.svg";
import dotIcon from "../../../../../../../../../utilities/images/package_plan_checkout/icon-dot.svg";
import "./StorePlanCheckout.scss";

const DURATIONS = [
  { months: 6, label: "6 months" },
  { months: 12, label: "1 year" },
  { months: 24, label: "2 years" },
];

const SECTION_IDS = { Learn: "7", Practice: "8", Test: "9" };

let cachedPlanVariants = null;

const readPlanVariants = (payload) => {
  const data = payload?.data?.data || payload?.data || payload;
  const variants =
    data?.bundle_plan?.build_plan?.variants ||
    data?.explore?.bundle_plan?.build_plan?.variants ||
    data?.build_plan?.variants ||
    [];
  return Array.isArray(variants) ? variants : [];
};

const formatRupee = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  const rounded = Number.isInteger(amount) ? amount : Math.round(amount);
  return `₹${rounded.toLocaleString("en-IN")}`;
};

const monthsFromName = (name = "") => {
  const text = String(name).toLowerCase();
  if (/6\s*months?/.test(text)) return 6;
  if (/2\s*years?/.test(text)) return 24;
  if (/1\s*year|12\s*months?/.test(text)) return 12;
  return null;
};

const validityFromName = (name = "") => {
  const months = monthsFromName(name);
  if (months === 6) return "6 months valid";
  if (months === 24) return "2 years valid";
  if (months === 12) return "1 year valid";
  return "";
};

const planCountFromName = (name = "") => {
  const match = String(name).match(/\(([^)]+)\)/);
  if (!match) return 0;
  return match[1]
    .split("+")
    .map((part) => part.trim())
    .filter(Boolean).length;
};

export const singleSectionLabel = (packageData) => {
  const name = String(packageData?.PackageInstallmentDetails?.package_name || "");
  if (!name || /\(l\s*\+\s*p\s*\+\s*t\)|\bmastery\b/i.test(name)) return "";
  if (/\(l\)/i.test(name)) return "Learn";
  if (/\(p\)/i.test(name)) return "Practice";
  if (/\(t\)/i.test(name)) return "Test";
  return "";
};

const LETTER_SECTION_IDS = { l: 7, p: 8, t: 9 };
const SECTION_NAMES = { 7: "Learn", 8: "Practice", 9: "Test" };

const normalizeSectionIds = (value) =>
  String(value || "")
    .split(",")
    .map((id) => Number(String(id).trim()))
    .filter(Boolean)
    .sort((a, b) => a - b)
    .join(",");

export const bundlePlanSectionIds = (packageData) => {
  const name = String(packageData?.PackageInstallmentDetails?.package_name || "");
  if (!name) return "";
  const match = name.match(/\(\s*([lpt](?:\s*\+\s*[lpt])*)\s*\)/i);
  if (match) {
    return normalizeSectionIds(
      match[1]
        .split("+")
        .map((letter) => LETTER_SECTION_IDS[letter.trim().toLowerCase()])
        .join(",")
    );
  }
  return /\bmastery\b/i.test(name) ? "7,8,9" : "";
};

export const bundlePlanLabel = (packageData) => {
  const ids = bundlePlanSectionIds(packageData);
  if (!ids) return "";
  if (ids === "7,8,9") return "Mastery";
  return ids
    .split(",")
    .map((id) => SECTION_NAMES[id])
    .join(" + ");
};

const StorePlanCheckout = ({ packageData, sectionLabel, upgradeCredit = 0 }) => {
  const history = useHistory();
  const details = packageData?.PackageInstallmentDetails || {};
  const offer = packageData?.package_upgrades?.offer || {};
  const showUpgrade =
    Number(packageData?.package_upgrades?.show_upgrade_banner) === 1 &&
    bundlePlanSectionIds(packageData) !== "7,8,9";
  const masteryId = offer.upgrade_package_id;
  const currentId = details.package_id;
  const [variants, setVariants] = useState(cachedPlanVariants || []);
  const [masteryPay, setMasteryPay] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (cachedPlanVariants) {
      setVariants(cachedPlanVariants);
      return undefined;
    }
    axioInstance
      .get(url_304)
      .then((response) => {
        if (cancelled) return;
        cachedPlanVariants = readPlanVariants(response?.data);
        setVariants(cachedPlanVariants);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showUpgrade || !masteryId) {
      setMasteryPay(null);
      return undefined;
    }
    let cancelled = false;
    setMasteryPay(null);
    axioInstance
      .get(`${url_store_package_deltaupgrade}?package_id=${masteryId}`)
      .then((response) => {
        if (cancelled) return;
        const quote = response?.data?.data;
        if (quote?.show_upgrade !== 1) return;
        const payload = quote.deltaupgrade_payload || {};
        const pay = Number(payload.delta_pay_rounded ?? quote.upgrade_card?.pay_amount);
        if (Number.isFinite(pay)) setMasteryPay(pay);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [showUpgrade, masteryId]);

  const currentVariant = variants.find(
    (variant) => String(variant.package_id) === String(currentId)
  );
  const currentMonths =
    Number(currentVariant?.duration) || monthsFromName(details.package_name) || 12;
  const activeIndex = Math.max(
    0,
    DURATIONS.findIndex((item) => item.months === currentMonths)
  );

  const coursePrice = Number(packageData?.price_details?.course_price || 0);
  const discount = Number(packageData?.price_details?.discount || 0);
  const payAmount = Math.max(0, coursePrice - discount - Number(upgradeCredit || 0));

  const masteryList = Math.max(0, Number(offer.amount || 0) - Number(offer.discount || 0));
  const masteryPrice =
    masteryPay != null && masteryPay < masteryList ? masteryPay : masteryList;
  const planCount = planCountFromName(offer.upgrade_package_name);
  const validity = validityFromName(offer.upgrade_package_name);
  const formattedDate = formatExpiryDate(details.date_expiry);
  const isPurchased = isPackageSubscribed(details);

  const openDuration = (months) => {
    if (months === currentMonths) return;
    const sectionIds = SECTION_IDS[sectionLabel] || bundlePlanSectionIds(packageData);
    const match = variants.find(
      (variant) =>
        normalizeSectionIds(variant.section_ids) === sectionIds &&
        Number(variant.duration) === months
    );
    if (match?.package_id && String(match.package_id) !== String(currentId)) {
      history.push(`/package/${match.package_id}`);
    }
  };

  if (isPurchased) {
    return (
      <aside className="user-dashboard-coupon-referral-wrapperr plan-checkout plan-checkout--owned">
        <div className="plan-checkout__owned">Purchased</div>
        {formattedDate ? (
          <p className="plan-checkout__validity">
            Valid up to <span>{formattedDate}</span>
          </p>
        ) : null}
      </aside>
    );
  }

  return (
    <aside className="user-dashboard-coupon-referral-wrapperr plan-checkout">
      <p className="plan-checkout__kicker">Your selection</p>
      <h2 className="plan-checkout__title">{sectionLabel}</h2>

      <div className="plan-checkout__duration">
        <p>Duration</p>
        <div className="plan-checkout__track">
          {DURATIONS.map((item) => {
            const selected = item.months === currentMonths;
            return (
              <button
                key={item.months}
                type="button"
                className={`plan-checkout__duration-btn${selected ? " is-current" : ""}`}
                aria-pressed={selected}
                onClick={() => openDuration(item.months)}
              >
                {item.label}
              </button>
            );
          })}
          <span
            className="plan-checkout__badge"
            style={{ left: `${((activeIndex + 0.5) / DURATIONS.length) * 100}%` }}
          >
            YOUR PLAN
          </span>
        </div>
      </div>

      <div className="plan-checkout__summary">
        <div className="plan-checkout__line">
          <span>{sectionLabel}</span>
          <span>{formatRupee(payAmount)}</span>
        </div>
        <img className="plan-checkout__divider" src={dividerIcon} alt="" />
        <div className="plan-checkout__pay">
          <span>To Pay</span>
          <strong>{formatRupee(payAmount)}</strong>
        </div>
      </div>

      <button
        type="button"
        className="plan-checkout__buy"
        onClick={() => history.push(`/package/${currentId}/checkout`)}
      >
        Buy Now
      </button>

      {showUpgrade && masteryId ? (
        <>
          <p className="plan-checkout__or">OR</p>
          <div className="plan-checkout__upgrade">
            <div>
              <strong>Upgrade to Mastery</strong>
              <p>
                {planCount > 0 ? <span>{planCount} plans</span> : null}
                {planCount > 0 && validity ? <img src={dotIcon} alt="" /> : null}
                {validity ? <span>{validity}</span> : null}
              </p>
            </div>
            <button
              type="button"
              onClick={() => history.push(`/package/${masteryId}/checkout`)}
            >
              <span>BUY NOW</span>
              {masteryPrice > 0 ? <span>{formatRupee(masteryPrice)}</span> : null}
            </button>
          </div>
        </>
      ) : null}
    </aside>
  );
};

export default StorePlanCheckout;
